import React, { memo, useCallback, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import CicCommentTemplatePicker from 'app/shared/components/CicCommentTemplatePicker';
import { cicCommentTemplatesPropTypes } from 'app/shared/components/CicCommentTemplatePicker/CicCommentTemplatePicker.propTypes';
import CicEnteredBy from 'app/shared/components/CicEnteredBy';
import CicValueChange from 'app/shared/components/CicValueChange';
import FieldHelper from 'app/shared/components/FieldHelper';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import PropTypes from 'prop-types';

import { CIC_SAVE_NOTE_MAX_LENGTH } from 'constants/cicReason';

import {
  COMMENT_LABEL,
  COMMENT_PLACEHOLDER,
  DisclosureTypeChipTitle,
  REASON_LABEL,
  REASON_PLACEHOLDER,
} from '../../CicFieldReasonsForm.constants';
import { CicFieldReasonsFormTestIds } from '../../CicFieldReasonsForm.testIds';
import { getCommentError, getReasonError } from '../../CicFieldReasonsForm.utils';
import CicReasonOption from '../CicReasonOption';

import {
  inputsStyles,
  reasonHelpStyles,
  rowStyles,
  summaryStyles,
} from './CicFieldReasonRow.styles';

const CicFieldReasonRow = ({
  change,
  reasons,
  value,
  isTouched,
  isDisabled,
  onReasonChange,
  onCommentChange,
  onBlur,
}) => {
  const { field, label, oldDisplay, newDisplay, changedByName, changedDate, disclosureType } =
    change;

  const selectedReason = useMemo(
    () => reasons.find(({ cicFeeReasonId }) => cicFeeReasonId === value.cicReasonId) ?? null,
    [reasons, value.cicReasonId],
  );

  const reasonError = isTouched ? getReasonError(value.cicReasonId) : '';
  const commentError = isTouched ? getCommentError(value.comment) : '';

  const onReasonSelect = useCallback(
    (e, option) => onReasonChange(field, option?.cicFeeReasonId ?? null),
    [field, onReasonChange],
  );

  const onCommentInput = useCallback(
    e => onCommentChange(field, e.target.value),
    [field, onCommentChange],
  );

  const onTemplateApply = useCallback(
    text => onCommentChange(field, text),
    [field, onCommentChange],
  );

  const onFieldBlur = useCallback(() => onBlur(field), [field, onBlur]);

  return (
    <Box data-testid={CicFieldReasonsFormTestIds.getRowId(field)} sx={rowStyles}>
      <Box sx={summaryStyles}>
        <Typography variant="bold14">{label}</Typography>

        <CicValueChange newDisplay={newDisplay} oldDisplay={oldDisplay} />

        <CicEnteredBy isCompact date={changedDate} name={changedByName} />

        <Box data-testid={CicFieldReasonsFormTestIds.getDisclosureLabelId(field)}>
          <LabelCDS size="S" title={DisclosureTypeChipTitle[disclosureType]} variant="BLUE" />
        </Box>
      </Box>

      <Box sx={inputsStyles}>
        <AutocompleteCDS
          disableClearable
          required
          OptionContent={CicReasonOption}
          dataTestId={CicFieldReasonsFormTestIds.getReasonSelectId(field)}
          dataTestIdInput={CicFieldReasonsFormTestIds.getReasonInputId(field)}
          dataTestIdOption={CicFieldReasonsFormTestIds.getReasonOptionId(field)}
          disabled={isDisabled}
          error={!!reasonError}
          label={REASON_LABEL}
          name={`${field}.cicReasonId`}
          optionKey="cicFeeReasonId"
          options={reasons}
          placeholder={REASON_PLACEHOLDER}
          value={selectedReason}
          variant="outlined"
          withSortedOptions={false}
          onBlur={onFieldBlur}
          onChange={onReasonSelect}
        />

        {!!(reasonError || selectedReason?.description) && (
          <Typography
            color={reasonError ? 'error.main' : 'text.secondary'}
            sx={reasonHelpStyles}
            variant="regular12"
          >
            {reasonError || selectedReason.description}
          </Typography>
        )}

        <CicCommentTemplatePicker
          commentTemplates={selectedReason?.commentTemplates}
          dataTestId={CicFieldReasonsFormTestIds.getTemplateSelectId(field)}
          dataTestIdInput={CicFieldReasonsFormTestIds.getTemplateInputId(field)}
          dataTestIdOption={CicFieldReasonsFormTestIds.getTemplateOptionId(field)}
          isDisabled={isDisabled}
          name={`${field}.commentTemplate`}
          reasonId={value.cicReasonId}
          onApply={onTemplateApply}
        />

        <Box>
          <TextFieldCDS
            multiline
            required
            dataTestId={CicFieldReasonsFormTestIds.getCommentInputId(field)}
            disabled={isDisabled}
            error={!!commentError}
            label={COMMENT_LABEL}
            maxLength={CIC_SAVE_NOTE_MAX_LENGTH}
            name={`${field}.comment`}
            placeholder={COMMENT_PLACEHOLDER}
            rows={3}
            value={value.comment}
            variant="outlined"
            onBlur={onFieldBlur}
            onChange={onCommentInput}
          />
          <FieldHelper
            error={commentError}
            length={value.comment.length}
            maxLength={CIC_SAVE_NOTE_MAX_LENGTH}
            text=""
          />
        </Box>
      </Box>
    </Box>
  );
};

CicFieldReasonRow.propTypes = {
  change: PropTypes.shape({
    field: PropTypes.string.isRequired,
    label: PropTypes.string,
    oldDisplay: PropTypes.string,
    newDisplay: PropTypes.string,
    changedByName: PropTypes.string,
    changedDate: PropTypes.string,
    disclosureType: PropTypes.string,
  }).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isTouched: PropTypes.bool.isRequired,
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      cicFeeReasonId: PropTypes.string.isRequired,
      name: PropTypes.string,
      description: PropTypes.string,
      commentTemplates: cicCommentTemplatesPropTypes,
    }),
  ).isRequired,
  value: PropTypes.shape({
    cicReasonId: PropTypes.string,
    comment: PropTypes.string,
  }).isRequired,
  onBlur: PropTypes.func.isRequired,
  onCommentChange: PropTypes.func.isRequired,
  onReasonChange: PropTypes.func.isRequired,
};

export default memo(CicFieldReasonRow);
